/** « Votre examen blanc gratuit » (D-172): the one complete mock exam of an account without a
 * subscription, on every exam's home: what it contains and its state (available, in progress,
 * done). Every value comes from the server (`academy/free-mock`, data of the Academy settings). */

import { getAcademyAttemptsListQueryKey, useAcademyFreeMockCreate, useAcademyFreeMockRetrieve } from "@agencyos/api-client";
import { asFreeMockContent } from "@agencyos/core";
import { Button, buttonClass } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { ClipboardCheck, Play } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { useActiveExam } from "../ielts/exam";
import "../coaching/coaching.css";

export function FreeMockCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { exam } = useActiveExam();
  const query = useAcademyFreeMockRetrieve({ exam }, { query: { retry: false } });
  const start = useAcademyFreeMockCreate();
  const data = query.data?.status === 200 ? query.data.data : undefined;
  if (!data || data.state === "not_offered") return null;
  const content = asFreeMockContent(data.mock);
  const go = () =>
    start.mutate(
      { data: { exam } },
      {
        onSuccess: (response) => {
          if (response.status !== 201) return;
          void queryClient.invalidateQueries({ queryKey: getAcademyAttemptsListQueryKey() });
          navigate(`/academy/attempt/${response.data.id}`);
        },
      },
    );
  return (
    <section className="ac-surface ac-card co-free" aria-labelledby="free-mock-title" data-testid="free-mock" data-state={data.state}>
      <h2 id="free-mock-title" className="ac-title-3">
        <ClipboardCheck aria-hidden size={16} /> {t("freeMock.title")}
      </h2>
      {data.state === "not_ready" ? <p className="ac-secondary">{t("freeMock.notReady")}</p> : null}
      {data.state === "needs_level" ? <p className="ac-secondary">{t("freeMock.needsLevel")}</p> : null}
      {content && data.state === "available" ? (
        <>
          <p className="ac-secondary">{t("freeMock.intro", { minutes: content.minutes })}</p>
          <ul>
            {content.sections.map((section) => (
              <li key={section.skill}>
                {t(`freeMock.skill.${section.skill}`)} · {t("freeMock.minutes", { count: section.minutes })}
              </li>
            ))}
          </ul>
          <p className="ac-footnote">{t("freeMock.once")}</p>
          <Button icon={<Play aria-hidden size={16} />} loading={start.isPending} onClick={go}>
            {t("freeMock.start")}
          </Button>
          {start.isError ? (
            <p className="ko" role="alert">
              {t("common.error")}
            </p>
          ) : null}
        </>
      ) : null}
      {data.state === "in_progress" && data.attempt ? (
        <>
          <p className="ac-secondary">{t("freeMock.inProgress")}</p>
          <Link className={buttonClass("primary")} to={`/academy/attempt/${data.attempt}`}>
            {t("freeMock.resume")}
          </Link>
        </>
      ) : null}
      {data.state === "done" && data.attempt ? <FreeMockDone attempt={data.attempt} delayed={data.delayed.length > 0} /> : null}
    </section>
  );
}

function FreeMockDone({ attempt, delayed }: { attempt: string; delayed: boolean }) {
  const { t } = useTranslation();
  return (
    <>
      <p className="ac-secondary">{t("freeMock.done")}</p>
      {delayed ? <p className="ac-footnote">{t("freeMock.delayed")}</p> : null}
      <div className="row wrap">
        <Link className={buttonClass("secondary")} to={`/academy/attempt/${attempt}/results`}>
          {t("freeMock.results")}
        </Link>
        <Link className={buttonClass("primary")} to="/academy/plans">
          {t("freeMock.plans")}
        </Link>
      </div>
    </>
  );
}

/** Under the results of the free mock: the next step is a plan (no second free mock). */
export function FreeMockNext({ attempt }: { attempt: string }) {
  const { t } = useTranslation();
  const { exam } = useActiveExam();
  const query = useAcademyFreeMockRetrieve({ exam }, { query: { retry: false } });
  const data = query.data?.status === 200 ? query.data.data : undefined;
  if (!data || data.state !== "done" || data.attempt !== attempt) return null;
  return (
    <section className="ac-surface ac-card co-free" aria-labelledby="free-mock-next" data-testid="free-mock-next">
      <h2 id="free-mock-next" className="ac-title-3">
        {t("freeMock.nextTitle")}
      </h2>
      <p className="ac-secondary">{t("freeMock.nextText")}</p>
      <Link className={buttonClass("primary")} to="/academy/plans">
        {t("freeMock.plans")}
      </Link>
    </section>
  );
}
